import type { Phrase } from './copy.js';

/** The gate, the walk through the compound, and the station up close (S1/S3/S4/S5). */
export const LOBBY = {
  /* ── S1 · The gate ────────────────────────────────────────────────────── */
  'lobby.splash.strap': { tl: 'Isang compound, anim na mesa.', en: 'One compound, six tables.' },
  'lobby.boot.opening': { tl: 'Binubuksan ang gate…', en: 'Opening the gate…' },
  'lobby.boot.reconnecting': { tl: 'Binabalik ang koneksyon…', en: 'Getting you back online…' },
  'lobby.boot.closed': {
    tl: 'Walang koneksyon. Suriin ang internet mo, tapos buksan ulit ang app.',
    en: 'No connection. Check your internet, then open the app again.',
  },

  /* ── The six stations ─────────────────────────────────────────────────────
   * The game titles are proper nouns and stay in the component; where a venue
   * sits, and the line it says to you, do not.
   */

  'lobby.venue.dama.tag': {
    tl: 'Tansan sa plywood, ilaw ng poste.',
    en: 'Bottle caps on plywood, under the streetlight.',
  },
  'lobby.venue.tongits.tag': {
    tl: 'Tatlo lang tayo. Sapaw agad.',
    en: 'Just the three of us. Top it fast.',
  },
  'lobby.venue.pusoydos.tag': { tl: 'Hawak mo ba ang mesa?', en: 'Can you hold the table?' },
  'lobby.venue.mahjong.tag': {
    tl: 'Labing-anim na tile. Seryosong mesa.',
    en: 'Sixteen tiles. Serious table.',
  },
  'lobby.venue.pusoy.tag': { tl: 'Tatlong hanay. Isipin mo muna.', en: 'Three rows. Think first.' },
  'lobby.venue.gera.tag': {
    tl: 'Walang nakakaalam ng ranggo mo.',
    en: 'Nobody knows your rank.',
  },

  /* ── Stake tiers ──────────────────────────────────────────────────────────
   * Not proper nouns: barya is loose change, datu is the big man. English says
   * the ladder plainly, and never in money — the numbers beside them are chips.
   */

  /* ── S3 · The compound ────────────────────────────────────────────────── */
  'lobby.rail.strap': {
    tl: 'Gabi na. Anim na mesa ang bukas.',
    en: 'It is night. Six tables are open.',
  },
  'lobby.rail.store': { tl: 'Tindahan', en: 'Store' },
  'lobby.rail.wallet': { tl: 'Pitaka', en: 'Wallet' },
  'lobby.rail.kubo': { tl: 'Kubo', en: 'Private room' },
  'lobby.rail.kubo.label': { tl: 'Kubo — private room', en: 'Private room' },
  'lobby.rail.settings': { tl: 'Settings', en: 'Settings' },
  'lobby.rail.settings.label': {
    tl: 'Settings at responsableng laro',
    en: 'Settings and responsible play',
  },
  'lobby.hint.swipe': {
    tl: 'I-swipe pakaliwa para maglakad sa compound.',
    en: 'Swipe left to walk the compound.',
  },
  'lobby.nav.prev': { tl: 'Balik sa naunang mesa', en: 'Back to the previous table' },
  'lobby.nav.next': { tl: 'Sunod na mesa', en: 'Next table' },

  'lobby.station.aria': { tl: '{game} — {station}', en: '{game} — {station}' },
  'lobby.station.playing': { tl: '{n} naglalaro', en: '{n} playing' },
  'lobby.station.checking': { tl: 'Tinitingnan…', en: 'Checking…' },
  'lobby.station.seats': { tl: '{n} upuan', en: '{n} seats' },
  'lobby.station.open': { tl: '{n} mesang bukas', en: '{n} tables open' },
  'lobby.station.soon': { tl: 'Malapit na', en: 'Coming soon' },
  'lobby.station.offline': { tl: 'Walang koneksyon', en: 'Offline' },

  'lobby.cta.soon': { tl: 'Hindi pa bukas ang mesang ito.', en: 'This table is not open yet.' },
  'lobby.cta.offline': {
    tl: 'Kailangan ng internet para tumaya.',
    en: 'You need a connection to play.',
  },
  'lobby.cta.loading': {
    tl: 'Tinitingnan kung may bukas na mesa…',
    en: 'Checking for an open table…',
  },
  'lobby.cta.empty': { tl: 'Wala pang laro — ikaw mag-umpisa.', en: 'No game yet — you start it.' },
  'lobby.cta.enter': { tl: 'Lapitan ang mesa', en: 'Walk up to the table' },

  /* ── S4 · The venue ───────────────────────────────────────────────────── */
  'lobby.venue.meta': { tl: '{n} manlalaro · {secs}s kada tira', en: '{n} players · {secs}s a turn' },
  'lobby.venue.shut.offline': {
    tl: 'Kailangan ng internet para tumaya. Mababasa mo pa rin ang mesa, pero hindi ka muna makakaupo.',
    en: 'You need a connection to play. You can still read the table, but you cannot sit down yet.',
  },
  'lobby.venue.shut.loading': {
    tl: 'Tinitingnan kung bukas ang mesang ito…',
    en: 'Checking whether this table is open…',
  },
  'lobby.venue.shut.soon': {
    tl: 'Hindi pa bukas ang mesang ito. Subukan ang ibang mesa sa compound.',
    en: 'This table is not open yet. Try another table in the compound.',
  },
  'lobby.venue.tables.error': {
    tl: 'Hindi ma-load ang listahan ng mesa. Suriin ang koneksyon.',
    en: 'Could not load the table list. Check your connection.',
  },

  'lobby.queue.in': { tl: 'Nasa pila ka — {tier}', en: 'You are in the queue — {tier}' },
  'lobby.queue.detail': {
    tl: 'Pang-{n} ka sa pila · {eta} ang hintay. May AI na kalaro kapag matagal — laging nakatatak na “AI” ang puck nila.',
    en: 'You are #{n} in line · {eta} to wait. AI players sit in if it drags — their puck always reads “AI”.',
  },
  'lobby.queue.leave': { tl: 'Alis sa pila', en: 'Leave the queue' },

  /* ── The room list ────────────────────────────────────────────────────── */
  'lobby.rooms.title': { tl: 'Mga mesa ngayon', en: 'Tables right now' },
  'lobby.rooms.sub': {
    tl: 'Pumili ka ng mesa, o hayaan mong ipares ka sa taas.',
    en: 'Pick a table, or get matched up above.',
  },
  'lobby.rooms.stake': { tl: '{tier} · {chips}', en: '{tier} · {chips}' },
  'lobby.rooms.seated': { tl: '{taken} sa {total} ang upo', en: '{taken} of {total} seated' },
  'lobby.rooms.sit': { tl: 'Upo dito', en: 'Sit here' },
  'lobby.rooms.watch': { tl: 'Panoorin', en: 'Watch' },
  'lobby.rooms.watching': { tl: 'Nanonood ka bilang miron.', en: 'You are watching this table.' },
  'lobby.rooms.full': { tl: 'Puno', en: 'Full' },
  'lobby.rooms.shut': { tl: 'Sarado', en: 'Closed' },
  'lobby.rooms.empty': {
    tl: 'Walang tao pa — ikaw ang mag-uumpisa.',
    en: 'Nobody here yet — you start it.',
  },
  'lobby.rooms.allai': { tl: 'Puro AI', en: 'All AI' },
  'lobby.rooms.two': { tl: '{a} at {b}', en: '{a} and {b}' },
  'lobby.rooms.withbots': { tl: '{who} · {n} AI', en: '{who} · {n} AI' },

  /* ── The private table ────────────────────────────────────────────────── */
  'lobby.kubo.title': { tl: 'Sarilinang mesa', en: 'Private table' },
  'lobby.kubo.sub': { tl: 'Barkada lang ang nakakapasok.', en: 'Only your crew gets in.' },
  'lobby.kubo.note': {
    tl: 'Gumawa ng Kubo o sumali sa code ng kabarkada. Barya at Bente lang ang pwede sa Kubo — paliwanag nasa loob.',
    en: 'Make a private room or join a friend’s code. Low and Mid stakes only — the reason is inside.',
  },
  'lobby.kubo.open': { tl: 'Buksan ang Kubo', en: 'Open a private room' },

  /* ── The stake tier cards ─────────────────────────────────────────────── */
  'lobby.tier.tong': { tl: 'Tong {pct}', en: 'Rake {pct}' },
  'lobby.tier.band': { tl: 'Buy-in na chips', en: 'Buy-in, in chips' },
  'lobby.tier.room': { tl: '{n} mesang may bakante', en: '{n} tables have room' },
  'lobby.tier.allfull': { tl: 'Puno lahat — pipila ka', en: 'All full — you will queue' },
  'lobby.tier.none': {
    tl: 'Wala pang laro dito — ikaw mag-umpisa.',
    en: 'No game here yet — you start it.',
  },
  'lobby.tier.nokubo': { tl: 'Walang Kubo sa Datu', en: 'No private rooms here' },
  'lobby.tier.sit': { tl: 'Upo na', en: 'Sit down' },
  'lobby.tier.running.one': {
    tl: '{n} larong tumatakbo — panoorin sa listahan sa baba.',
    en: '{n} game running — watch it in the list below.',
  },
  'lobby.tier.running': {
    tl: '{n} larong tumatakbo — panoorin sa listahan sa baba.',
    en: '{n} games running — watch them in the list below.',
  },
  'lobby.tier.quiet': { tl: 'Walang laro ngayon dito.', en: 'No games running here.' },

  /* ── S5 · Buy-in ──────────────────────────────────────────────────────── */
  'lobby.buyin.title': { tl: 'Upo ka na', en: 'Take a seat' },
  'lobby.buyin.amount': { tl: 'chips na buy-in · {tier}', en: 'chips buy-in · {tier}' },
  'lobby.buyin.where': { tl: '{game} · {station} · {n} manlalaro.', en: '{game} · {station} · {n} players.' },
  'lobby.buyin.pick': { tl: 'Piliin ang buy-in ({band})', en: 'Pick your buy-in ({band})' },
  'lobby.buyin.iffull': { tl: 'Kung mapuno ang mesa', en: 'If the table fills' },
  'lobby.buyin.playfor': { tl: 'ang pinaglalabanan', en: 'up for grabs' },
  'lobby.buyin.potnote': {
    tl: 'Pot {pot} · tong {pct} ({tong}), nakalista sa resibo pagkatapos.',
    en: 'Pot {pot} · rake {pct} ({tong}), itemized on the receipt after.',
  },
  'lobby.buyin.wallet': { tl: 'Nasa pitaka mo', en: 'In your wallet' },
  'lobby.buyin.short': {
    tl: 'Kulang ang chips mo para sa buy-in na ito.',
    en: 'You do not have enough chips for this buy-in.',
  },
  'lobby.buyin.shortnote': {
    tl: 'Kailangan mo pa ng {n}. Kunin ang libreng baon sa pitaka, mag-top up sa tindahan, o hilahin pababa ang buy-in.',
    en: 'You need {n} more. Grab your free daily chips in the wallet, top up at the store, or drag the buy-in down.',
  },
  'lobby.buyin.baon': { tl: 'Kunin ang baon', en: 'Get daily chips' },
  'lobby.buyin.store': { tl: 'Punta sa tindahan', en: 'Go to the store' },
  'lobby.buyin.offline': {
    tl: 'Walang koneksyon ngayon. Balik ka kapag bumalik ang signal.',
    en: 'No connection right now. Come back when your signal does.',
  },
  'lobby.buyin.sit': { tl: 'Upo na — {chips} chips', en: 'Sit down — {chips} chips' },
  'lobby.buyin.solo': { tl: 'Solo kontra AI', en: 'Solo vs AI' },
  'lobby.buyin.cancel': { tl: 'Hindi muna', en: 'Not now' },
  /* Singulars: English inflects after a count and Taglish does not. */
  'lobby.station.open.one': { tl: '{n} mesang bukas', en: '{n} table open' },
  'lobby.tier.room.one': { tl: '{n} mesang may bakante', en: '{n} table has room' },
} as const satisfies Record<string, Phrase>;
